import { useState } from "react";
import { Link } from "react-router";
import { PillView, useAction } from "../../components/ui";
import { api } from "../../data";
import { t } from "../../lib/sprache";
import { useClientErrors, useMailHealth, useOpsStatus, useSenderDomains, useStorageCleanup } from "./hooks";
import { countLabel, utcDateTime } from "./model";
import { DemoHinweis, Fehler, Laden } from "./parts";
import {
  alertText, clientErrorKindLabel, clientErrorPill, clientErrorPortalLabel, functionName, httpFunctionPill, httpStatusText, jobDescription, jobPill, mailHealthPill, opsKpis, opsNotices, removalReasonLabel, senderDomainPill,
  sentLabel, sentPill, waitText, QUEUE_STUCK_MINUTES, type OpsStatus,
} from "./ueberwachung-model";

/**
 * Betrieb (0101): Zeitpläne (pg_cron), Aufrufe von Serverfunktionen aus Zeitplänen (pg_net), gescheiterte Mails je Agentur,
 * Warteschlange und die Mails an den Betrieb (täglicher Bericht, Sofortwarnungen). Nur Zählwerte und Fehlermeldungen, keine
 * Inhalte der Agenturen. Bausteine des Pakets (panel, grid, metric, tablewrap, pill, notice).
 */
export function Ueberwachung() {
  const q = useOpsStatus();
  const [nowMs] = useState(() => Date.now());
  const head = (
    <>
      <title>{t("Betrieb · Plattform · CreatorOffice", "Operations · Platform · CreatorOffice")}</title>
      <h1>{t("Betrieb", "Operations")}</h1>
    </>
  );
  if (q.error) return <>{head}<Fehler error={q.error} retry={() => void q.refetch()} /></>;
  if (q.isPending || !q.data) return <>{head}<Laden /></>;
  const s = q.data;
  const k = opsKpis(s, nowMs);
  return (
    <>
      {head}
      <p>{t(`Zeitpläne, Serverfunktionen und Mailversand der letzten ${s.hours} Stunden. Jeden Morgen geht ein Betriebsbericht an support_email, bei einer Häufung von Fehlern sofort eine Warnung.`,
        `Scheduled jobs, edge functions and email sending of the last ${s.hours} hours. Every morning an operations report goes to support_email, and a warning goes out immediately when errors pile up.`)}</p>
      {opsNotices(s).map((n) => <div key={n.text} className={n.tone === "warning" ? "notice warning" : "notice"} role={n.tone === "warning" ? "alert" : undefined}>{n.text}</div>)}

      {s.alerts.length > 0 && (
        <section className="panel stack" aria-labelledby="warnungen-titel">
          <h2 id="warnungen-titel">{t("Aktive Warnungen", "Active warnings")}</h2>
          <ul>
            {s.alerts.map((a) => <li key={a.occasion}><span className="pill error"><span className="pill-zeichen">!</span> {alertText(a)}</span>{a.message && <small> {a.message}</small>}</li>)}
          </ul>
          <small>{t(`Je Anlass geht höchstens alle ${s.settings.cooldown_hours} Stunden eine Warnung an support_email.`, `At most one warning per occasion every ${s.settings.cooldown_hours} hours goes to support_email.`)}</small>
        </section>
      )}

      <div className="grid four">
        <section className="panel stack">
          <h2>{t("Zeitpläne", "Scheduled jobs")}</h2>
          <div className="metric">{countLabel(k.cronFailures)}</div>
          <p>{s.cron.available
            ? t(`Fehlschläge in ${s.hours} h · ${k.cronStreaks} mit Fehlern in Folge · ${k.missingJobs} fehlen`, `failures in ${s.hours} h · ${k.cronStreaks} failing in a row · ${k.missingJobs} missing`)
            : t("pg_cron nicht verfügbar", "pg_cron not available")}</p>
        </section>
        <section className="panel stack">
          <h2>{t("Serverfunktionen", "Edge functions")}</h2>
          <div className="metric">{countLabel(k.httpFailures)}</div>
          <p>{s.http.available ? t(`fehlgeschlagene Aufrufe aus Zeitplänen in ${s.hours} h`, `failed calls from jobs in ${s.hours} h`) : t("pg_net nicht verfügbar", "pg_net not available")}</p>
        </section>
        <section className="panel stack">
          <h2>{t("Mails", "Emails")}</h2>
          <div className="metric">{countLabel(k.mailFailed + k.mailBounced)}</div>
          <p>{t(`${k.mailFailed} gescheitert, ${k.mailBounced} zurückgekommen in ${s.hours} h · ${s.mail.failed_last_hour} in der letzten Stunde`,
            `${k.mailFailed} failed, ${k.mailBounced} bounced in ${s.hours} h · ${s.mail.failed_last_hour} in the last hour`)}</p>
        </section>
        <section className="panel stack">
          <h2>{t("Warteschlange", "Queue")}</h2>
          <div className="metric">{countLabel(k.pending)}</div>
          <p>{waitText(k.oldestMinutes)}</p>
          {k.stuck && <span className="pill warning"><span className="pill-zeichen">◷</span> {t(`Stau: länger als ${QUEUE_STUCK_MINUTES} Minuten`, `Backlog: longer than ${QUEUE_STUCK_MINUTES} minutes`)}</span>}
        </section>
      </div>

      <Zeitplaene s={s} />
      <Funktionen s={s} />
      <FehlerImBrowser />
      <MailsJeAgentur s={s} />
      <Zustellbarkeit />
      <Absenderdomains />
      <SpeicherAufraeumen />
      <Betriebsmails s={s} />
      <DemoHinweis />
    </>
  );
}

function Zeitplaene({ s }: { s: OpsStatus }) {
  const thr = s.settings.consecutive_threshold;
  return (
    <section className="panel stack" aria-labelledby="zeitplaene-titel">
      <h2 id="zeitplaene-titel">{t("Zeitpläne", "Scheduled jobs")}</h2>
      {!s.cron.available ? <p>{t("pg_cron ist in dieser Datenbank nicht eingerichtet.", "pg_cron is not set up in this database.")}</p> : (
        <>
          <div className="tablewrap">
            <table>
              <caption className="sr-only">{t("Zeitpläne mit letztem Lauf", "Scheduled jobs with last run")}</caption>
              <thead><tr>
                <th scope="col">{t("Zeitplan", "Job")}</th><th scope="col">{t("Rhythmus", "Schedule")}</th>
                <th scope="col">{t("Letzter Lauf", "Last run")}</th><th scope="col">{t("Stand", "State")}</th>
              </tr></thead>
              <tbody>
                {s.cron.jobs.map((j) => (
                  <tr key={j.jobid}>
                    <td><strong>{j.jobname}</strong>{jobDescription(j) && <small>{jobDescription(j)}</small>}</td>
                    <td><code>{j.schedule}</code></td>
                    <td>{utcDateTime(j.last_start)}{j.last_status === "failed" && j.last_message && <small className="tk-error">{j.last_message}</small>}</td>
                    <td><PillView pill={jobPill(j, thr)} /></td>
                  </tr>
                ))}
                {s.cron.jobs.length === 0 && <tr><td colSpan={4}>{t("Keine Zeitpläne angelegt.", "No scheduled jobs set up.")}</td></tr>}
              </tbody>
            </table>
          </div>
          {s.cron.missing.length > 0 && (
            <div className="stack">
              <h3>{t("Fehlende Zeitpläne", "Missing jobs")}</h3>
              <ul>
                {s.cron.missing.map((m) => (
                  <li key={m.jobname}>
                    <code>{m.jobname}</code> · {jobDescription(m)}
                    {m.manual ? <small> {t("von Hand anlegen (docs/EINSPIELPLAN.md Abschnitt 6)", "create by hand (docs/EINSPIELPLAN.md section 6)")}</small>
                      : <small> {t("legt die Migration an, wenn pg_cron beim Einspielen vorhanden ist", "created by the migration when pg_cron exists at deploy time")}</small>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <h3>{t(`Fehlgeschlagene Läufe · ${s.hours} h`, `Failed runs · ${s.hours} h`)}</h3>
          <div className="tablewrap">
            <table>
              <caption className="sr-only">{t("Fehlgeschlagene Läufe", "Failed runs")}</caption>
              <thead><tr><th scope="col">{t("Zeitpunkt", "Time")}</th><th scope="col">{t("Zeitplan", "Job")}</th><th scope="col">{t("Meldung", "Message")}</th></tr></thead>
              <tbody>
                {s.cron.failures.map((f) => (
                  <tr key={`${f.jobid}-${f.start_time}`}><td>{utcDateTime(f.start_time)}</td><td>{f.jobname ?? `#${f.jobid}`}</td><td>{f.message ?? "–"}</td></tr>
                ))}
                {s.cron.failures.length === 0 && <tr><td colSpan={3}>{t("Keine Fehlschläge.", "No failures.")}</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

function Funktionen({ s }: { s: OpsStatus }) {
  const thr = s.settings.consecutive_threshold;
  return (
    <section className="panel stack" aria-labelledby="funktionen-titel">
      <h2 id="funktionen-titel">{t("Serverfunktionen aus Zeitplänen", "Edge functions called by jobs")}</h2>
      {!s.http.available ? <p>{t("pg_net ist in dieser Datenbank nicht eingerichtet.", "pg_net is not set up in this database.")}</p> : (
        <>
          <p>{t("Aufrufe über app.edge_function_call tragen den Namen der Funktion; Zeitpläne, die net.http_post direkt aufrufen, erscheinen ohne Zuordnung. pg_net bewahrt Antworten nur einige Stunden auf.",
            "Calls via app.edge_function_call carry the function name; jobs calling net.http_post directly appear unassigned. pg_net keeps responses for a few hours only.")}</p>
          <div className="tablewrap">
            <table>
              <caption className="sr-only">{t("Serverfunktionen mit letztem Aufruf", "Edge functions with last call")}</caption>
              <thead><tr><th scope="col">{t("Funktion", "Function")}</th><th scope="col">{t("Letzter Aufruf", "Last call")}</th><th scope="col">{t("Antwort", "Response")}</th><th scope="col">{t("Stand", "State")}</th></tr></thead>
              <tbody>
                {s.http.functions.map((f) => (
                  <tr key={f.function ?? "ohne"}>
                    <td>{functionName(f.function)}</td>
                    <td>{utcDateTime(f.last_at)}</td>
                    <td>{httpStatusText({ status_code: f.last_status, timed_out: f.last_timed_out })}{f.last_failed && f.last_error && <small className="tk-error">{f.last_error}</small>}</td>
                    <td><PillView pill={httpFunctionPill(f, thr)} /></td>
                  </tr>
                ))}
                {s.http.functions.length === 0 && <tr><td colSpan={4}>{t("Keine Aufrufe in den letzten Tagen.", "No calls in the last days.")}</td></tr>}
              </tbody>
            </table>
          </div>
          <h3>{t(`Fehlgeschlagene Aufrufe · ${s.hours} h`, `Failed calls · ${s.hours} h`)}</h3>
          <div className="tablewrap">
            <table>
              <caption className="sr-only">{t("Fehlgeschlagene Aufrufe", "Failed calls")}</caption>
              <thead><tr><th scope="col">{t("Zeitpunkt", "Time")}</th><th scope="col">{t("Funktion", "Function")}</th><th scope="col">{t("Antwort", "Response")}</th><th scope="col">{t("Meldung", "Message")}</th></tr></thead>
              <tbody>
                {s.http.failures.map((f) => (
                  <tr key={f.id}><td>{utcDateTime(f.created)}</td><td>{functionName(f.function)}</td><td>{httpStatusText(f)}</td><td>{f.error ?? f.content ?? "–"}</td></tr>
                ))}
                {s.http.failures.length === 0 && <tr><td colSpan={4}>{t("Keine Fehler.", "No errors.")}</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

function MailsJeAgentur({ s }: { s: OpsStatus }) {
  return (
    <section className="panel stack" aria-labelledby="mails-titel">
      <h2 id="mails-titel">{t("Gescheiterte Mails je Agentur", "Failed emails per agency")}</h2>
      <div className="tablewrap">
        <table>
          <caption className="sr-only">{t("Gescheiterte und zurückgekommene Mails je Agentur", "Failed and bounced emails per agency")}</caption>
          <thead><tr>
            <th scope="col">{t("Agentur", "Agency")}</th><th scope="col">{t(`Gescheitert · ${s.hours} h`, `Failed · ${s.hours} h`)}</th>
            <th scope="col">{t(`Zurückgekommen · ${s.hours} h`, `Bounced · ${s.hours} h`)}</th><th scope="col">{t("7 Tage", "7 days")}</th>
            <th scope="col">{t("Letzter Fehler", "Last error")}</th>
          </tr></thead>
          <tbody>
            {s.mail.by_org.map((m) => (
              <tr key={m.org_id ?? "betrieb"}>
                <td>{m.org_id ? <Link to={`/intern/agenturen/${m.org_id}`}>{m.name ?? m.org_id}</Link> : t("Mails an den Betrieb", "Emails to operations")}</td>
                <td><span className="num">{countLabel(m.failed)}</span></td>
                <td><span className="num">{countLabel(m.bounced)}</span></td>
                <td><span className="num">{countLabel(m.failed_7d)}</span></td>
                <td>{m.last_error ?? "–"}<small>{utcDateTime(m.last_failed_at)}</small></td>
              </tr>
            ))}
            {s.mail.by_org.length === 0 && <tr><td colSpan={5}>{t("In den letzten 7 Tagen ist keine Mail gescheitert.", "No email failed in the last 7 days.")}</td></tr>}
          </tbody>
        </table>
      </div>
      <small>{t("Einzelheiten sieht die Agentur in ihrem Postausgang; die Konsole zeigt nur Zählwerte und die letzte Fehlermeldung.",
        "The agency sees details in its outbox; the console only shows counts and the last error message.")}</small>
    </section>
  );
}

function Betriebsmails({ s }: { s: OpsStatus }) {
  const st = s.settings;
  return (
    <section className="panel stack" aria-labelledby="betriebsmails-titel">
      <h2 id="betriebsmails-titel">{t("Mails an den Betrieb", "Emails to operations")}</h2>
      <p>
        {st.support_email ? t(`An ${st.support_email}`, `To ${st.support_email}`) : t("Keine Adresse hinterlegt", "No address set")}
        {` · ${st.language === "en" ? t("Englisch", "English") : t("Deutsch", "German")} · ${st.time_zone}`}
      </p>
      <small>{t(`Täglicher Bericht gegen 05:00 UTC. Sofortwarnung bei mehr als ${st.failed_mails_threshold} gescheiterten Mails in einer Stunde oder wenn ein Zeitplan bzw. eine Serverfunktion ${st.consecutive_threshold}-mal hintereinander scheitert; höchstens alle ${st.cooldown_hours} Stunden je Anlass. Einstellbar unter Produkt-Standards (support_email, ops_…).`,
        `Daily report around 05:00 UTC. Instant warning when more than ${st.failed_mails_threshold} emails fail within an hour or a job or edge function fails ${st.consecutive_threshold} times in a row; at most every ${st.cooldown_hours} hours per occasion. Adjustable under product defaults (support_email, ops_…).`)}</small>
      <div className="tablewrap">
        <table>
          <caption className="sr-only">{t("Zuletzt verschickte Betriebsmails", "Recently sent operations emails")}</caption>
          <thead><tr><th scope="col">{t("Zeitpunkt", "Time")}</th><th scope="col">{t("Mail", "Email")}</th><th scope="col">{t("Stand", "State")}</th></tr></thead>
          <tbody>
            {s.sent.map((m) => (
              <tr key={`${m.occasion}-${m.sent_at}`}>
                <td>{utcDateTime(m.sent_at)}</td>
                <td>{sentLabel(m)}{m.error && <small className="tk-error">{m.error}</small>}</td>
                <td><PillView pill={sentPill(m)} /></td>
              </tr>
            ))}
            {s.sent.length === 0 && <tr><td colSpan={3}>{t("Noch keine Betriebsmail verschickt.", "No operations email sent yet.")}</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** Eigene Absenderdomains (0102): Stand je Agentur, offene Löschungen beim Versanddienst. */
function Absenderdomains() {
  const q = useSenderDomains();
  return (
    <section className="panel stack" aria-labelledby="absenderdomains-titel">
      <h2 id="absenderdomains-titel">{t("Eigene Absenderdomains", "Custom sender domains")}</h2>
      {q.error ? <Fehler error={q.error} retry={() => void q.refetch()} /> : q.isPending || !q.data ? <Laden /> : (
        <>
          <p>{q.data.ready
            ? t("Der Versand über eigene Domains ist freigeschaltet (custom_sender_domains_ready): bestätigte Domains gehen sofort in Betrieb.",
              "Sending via custom domains is enabled (custom_sender_domains_ready): verified domains go live right away.")
            : t("Der Versand über eigene Domains ist noch nicht freigeschaltet (Produkt-Standards · custom_sender_domains_ready). Bestätigte Domains warten, bis er eingeschaltet ist.",
              "Sending via custom domains is not enabled yet (product defaults · custom_sender_domains_ready). Verified domains wait until it is switched on.")}</p>
          <div className="tablewrap">
            <table>
              <caption className="sr-only">{t("Eigene Absenderdomains der Agenturen", "Custom sender domains of the agencies")}</caption>
              <thead><tr>
                <th scope="col">{t("Agentur", "Agency")}</th><th scope="col">{t("Absender", "Sender")}</th><th scope="col">{t("Stand", "State")}</th>
                <th scope="col">{t("Zuletzt geprüft", "Last checked")}</th>
              </tr></thead>
              <tbody>
                {q.data.domains.map((d) => (
                  <tr key={d.org_id}>
                    <td><Link to={`/intern/agenturen/${d.org_id}`}>{d.org_name}</Link>{!d.module && <small>{t("Tarif ohne Modul: wird beim nächsten Lauf entfernt", "Plan without module: removed on the next run")}</small>}</td>
                    <td><code>{d.local_part}@{d.domain}</code></td>
                    <td><PillView pill={senderDomainPill(d, q.data!.ready)} />{d.error && <small className="tk-error">{d.error}</small>}</td>
                    <td>{utcDateTime(d.checked_at)}</td>
                  </tr>
                ))}
                {q.data.domains.length === 0 && <tr><td colSpan={4}>{t("Keine Agentur nutzt eine eigene Absenderdomain.", "No agency uses a custom sender domain.")}</td></tr>}
              </tbody>
            </table>
          </div>
          {q.data.removals.length > 0 && (
            <div className="stack">
              <h3>{t("Beim Versanddienst noch zu entfernen", "Still to be removed at the email service")}</h3>
              <ul>
                {q.data.removals.map((r) => (
                  <li key={`${r.domain}-${r.requested_at}`}>
                    <code>{r.domain}</code> · {removalReasonLabel(r.reason)} · {utcDateTime(r.requested_at)}
                    {r.attempts > 0 && <small className="tk-error"> {t(`${r.attempts} Versuche`, `${r.attempts} attempts`)}{r.last_error ? `: ${r.last_error}` : ""}</small>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <small>{t("Anlegen, Prüfen und Entfernen übernimmt der Zeitplan talentkontor-absenderdomains (Serverfunktion sender-domain) alle 15 Minuten.",
            "The job talentkontor-absenderdomains (edge function sender-domain) creates, checks and removes domains every 15 minutes.")}</small>
        </>
      )}
    </section>
  );
}

/** Speicher aufräumen (0103): Einträge storage_pending mit Versuchen und letztem Fehler; erledigte mit Anzahl gelöschter Dateien. */
function SpeicherAufraeumen() {
  const q = useStorageCleanup();
  const open = (q.data ?? []).filter((l) => !l.resolved_at);
  return (
    <section className="panel stack" aria-labelledby="speicher-titel">
      <h2 id="speicher-titel">{t("Speicher aufräumen", "Storage cleanup")}</h2>
      <p>{t("Supabase lehnt direktes Löschen im Speicher ab. Dateien gekündigter und gelöschter Agenturen löscht deshalb der Zeitplan talentkontor-speicher (Serverfunktion storage-cleanup) täglich über die Storage-Schnittstelle; bei der Reduktion bleiben alle Belege.",
        "Supabase rejects direct deletion in storage. The job talentkontor-speicher (edge function storage-cleanup) therefore deletes files of cancelled and deleted agencies daily via the storage API; a reduction keeps all records.")}</p>
      {q.error ? <Fehler error={q.error} retry={() => void q.refetch()} /> : q.isPending || !q.data ? <Laden /> : (
        <div className="tablewrap">
          <table>
            <caption className="sr-only">{t("Einträge zum Aufräumen des Speichers", "Storage cleanup entries")}</caption>
            <thead><tr>
              <th scope="col">{t("Vermerkt", "Logged")}</th><th scope="col">{t("Agentur", "Agency")}</th><th scope="col">{t("Schritt", "Step")}</th>
              <th scope="col">{t("Stand", "State")}</th>
            </tr></thead>
            <tbody>
              {q.data.map((l) => {
                const d = l.resolved_detail ?? {};
                return (
                  <tr key={l.id}>
                    <td>{utcDateTime(l.at)}</td>
                    <td>{l.org_name ?? "–"}</td>
                    <td>{l.detail?.step === "reduce" ? t("Reduktion auf die Belege", "Reduction to the records") : t("Endgültige Löschung", "Final deletion")}</td>
                    <td>
                      {l.resolved_at
                        ? <span className="pill success"><span className="pill-zeichen">✓</span> {t(`Erledigt: ${d.deleted ?? 0} Dateien gelöscht`, `Done: ${d.deleted ?? 0} files deleted`)}</span>
                        : (d.attempts ?? 0) > 0
                          ? <span className="pill error"><span className="pill-zeichen">!</span> {t(`Offen nach ${d.attempts} Versuchen`, `Open after ${d.attempts} attempts`)}</span>
                          : <span className="pill warning"><span className="pill-zeichen">◷</span> {t("Offen, nächster Lauf löscht", "Open, next run deletes")}</span>}
                      {!l.resolved_at && d.last_error && <small className="tk-error">{d.last_error}</small>}
                      {l.resolved_at && <small>{utcDateTime(l.resolved_at)}</small>}
                    </td>
                  </tr>
                );
              })}
              {q.data.length === 0 && <tr><td colSpan={4}>{t("Nichts aufzuräumen.", "Nothing to clean up.")}</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {open.length > 0 && <small>{t(`${open.length} Einträge offen.`, `${open.length} entries open.`)}</small>}
    </section>
  );
}

/** Zustellbarkeit (0104): Stundenlimit je Agentur, gebremste Mails, Rückläuferquote der letzten 7 Tage. */
function Zustellbarkeit() {
  const q = useMailHealth();
  return (
    <section className="panel stack" aria-labelledby="zustellbarkeit-titel">
      <h2 id="zustellbarkeit-titel">{t("Zustellbarkeit", "Deliverability")}</h2>
      {q.error ? <Fehler error={q.error} retry={() => void q.refetch()} /> : q.isPending || !q.data ? <Laden /> : (
        <>
          <p>{t(`Je Agentur gehen höchstens ${q.data.limit} Mails in der Stunde raus (Testphase ${q.data.limit_trial}); weitere warten im Postausgang und gehen, sobald die Stunde Platz hat. Kommen mehr als ${q.data.bounce_percent} % der Mails der letzten 7 Tage zurück (ab ${q.data.bounce_min_mails} Mails), bekommt die Agentur eine Aufgabe.`,
            `Per agency at most ${q.data.limit} emails go out per hour (trial ${q.data.limit_trial}); more wait in the outbox and go out as soon as the hour has room. If more than ${q.data.bounce_percent} % of the emails of the last 7 days bounce (from ${q.data.bounce_min_mails} emails), the agency gets a task.`)}</p>
          <div className="tablewrap">
            <table>
              <caption className="sr-only">{t("Zustellbarkeit je Agentur", "Deliverability per agency")}</caption>
              <thead><tr>
                <th scope="col">{t("Agentur", "Agency")}</th><th scope="col">{t("Letzte Stunde", "Last hour")}</th><th scope="col">{t("Wartend", "Waiting")}</th>
                <th scope="col">{t("Rückläufer · 7 Tage", "Bounces · 7 days")}</th><th scope="col">{t("Stand", "State")}</th>
              </tr></thead>
              <tbody>
                {q.data.orgs.map((o) => (
                  <tr key={o.org_id}>
                    <td><Link to={`/intern/agenturen/${o.org_id}`}>{o.name}</Link></td>
                    <td><span className="num">{countLabel(o.sent_hour)} / {countLabel(o.hourly_limit)}</span></td>
                    <td><span className="num">{countLabel(o.pending)}</span></td>
                    <td>{o.sent_7d ? <span className="num">{countLabel(o.bounced_7d ?? 0)} / {countLabel(o.sent_7d)}</span> : "–"}</td>
                    <td><PillView pill={mailHealthPill(o, q.data!)} /></td>
                  </tr>
                ))}
                {q.data.orgs.length === 0 && <tr><td colSpan={5}>{t("In den letzten 7 Tagen keine Mails.", "No emails in the last 7 days.")}</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

/**
 * Fehler im Browser (0158): Abstürze und unbehandelte Fehler aus Portal und öffentlichen Seiten, gebündelt je Fingerabdruck.
 * Erledigt markieren blendet ein Bündel nach hinten; tritt der Fehler danach wieder auf, ist es wieder offen.
 */
function FehlerImBrowser() {
  const q = useClientErrors();
  const { busy, attempt } = useAction();
  const offen = (q.data?.groups ?? []).filter((g) => !g.resolved).length;
  return (
    <section className="panel stack" aria-labelledby="browserfehler-titel">
      <h2 id="browserfehler-titel">{t("Fehler im Browser", "Errors in the browser")}</h2>
      {q.error ? <Fehler error={q.error} retry={() => void q.refetch()} /> : q.isPending || !q.data ? <Laden /> : (
        <>
          <p>{t(`Abstürze und unbehandelte Fehler der letzten ${q.data.days} Tage, gebündelt; E-Mail-Adressen, Tokens, IBANs und lange Zahlen entfernt die Datenbank vor dem Speichern. Mehr als ${q.data.threshold} Meldungen in etwa einer Stunde lösen eine Sofortwarnung aus (Produkt-Standards · ops_alert_client_errors), offene Fehler stehen im Betriebsbericht.`,
            `Crashes and unhandled errors of the last ${q.data.days} days, grouped; the database removes email addresses, tokens, IBANs and long numbers before storing. More than ${q.data.threshold} reports in about an hour trigger an immediate warning (product defaults · ops_alert_client_errors), open errors appear in the daily report.`)}</p>
          <div className="tablewrap">
            <table className="tk-browserfehler">
              <caption className="sr-only">{t("Fehler im Browser", "Errors in the browser")}</caption>
              <thead><tr>
                <th scope="col">{t("Zuletzt", "Last seen")}</th><th scope="col">{t("Fehler", "Error")}</th><th scope="col">{t("Bereich", "Area")}</th>
                <th scope="col">{t("Häufigkeit", "Frequency")}</th><th scope="col">{t("Stand", "State")}</th>
              </tr></thead>
              <tbody>
                {q.data.groups.map((g) => (
                  <tr key={g.fingerprint}>
                    <td>{utcDateTime(g.last_at)}</td>
                    <td>
                      <strong>{g.message}</strong>
                      <small>{[clientErrorKindLabel(g.kind), g.path, g.source?.replace(/^https?:\/\/[^/]+/, ""), g.browser, g.release && `Release ${g.release}`].filter(Boolean).join(" · ")}</small>
                      {g.stack && (
                        <details className="tk-fehler-details">
                          <summary>{t("Stack", "Stack")}</summary>
                          <pre className="mono">{g.stack}</pre>
                        </details>
                      )}
                    </td>
                    <td>{clientErrorPortalLabel(g.portal)}{g.orgs.length > 0 && <small>{g.orgs.join(", ")}</small>}</td>
                    <td>
                      <span className="num">{g.hits}×</span>
                      <small>{t(`${g.people} ${g.people === 1 ? "Person" : "Personen"} · an ${g.days} ${g.days === 1 ? "Tag" : "Tagen"}`,
                        `${g.people} ${g.people === 1 ? "person" : "people"} · on ${g.days} ${g.days === 1 ? "day" : "days"}`)}</small>
                    </td>
                    <td className="tk-koop-aktion">
                      <div className="row">
                        <PillView pill={clientErrorPill(g)} />
                        <button type="button" className="btn" disabled={!!busy}
                          onClick={() => void attempt(`fehler:${g.fingerprint}`, () => api.rpc("platform_client_error_resolve", { p_fingerprint: g.fingerprint, p_resolved: !g.resolved }),
                            g.resolved ? t("Wieder offen.", "Reopened.") : t("Als erledigt markiert.", "Marked as resolved."))}>
                          {g.resolved ? t("Wieder öffnen", "Reopen") : t("Erledigt", "Resolve")}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {q.data.groups.length === 0 && <tr><td colSpan={5}>{t("Keine Fehler gemeldet.", "No errors reported.")}</td></tr>}
              </tbody>
            </table>
          </div>
          <small>{t(`${offen} offen · ${q.data.last_hour} Meldungen in etwa der letzten Stunde`, `${offen} open · ${q.data.last_hour} reports in about the last hour`)}</small>
        </>
      )}
    </section>
  );
}
